import { isCvImportReviewCancelled, isCvImportSourceChanged } from '../../lib/cv-import-review';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOnboarding } from './OnboardingProvider';
import {
  clearPostAuthTarget,
  readPostAuthTargetAsync,
} from '../../lib/auth-handoff';
import { useToast } from '../../hooks/useToast';
import {
  getErrorObservabilityProperties,
  trackProductEvent,
} from '../../lib/product-analytics';
import {
  claimResumeHandoff,
  clearResumeHandoffToken,
  getTerminalResumeHandoffErrorCode,
  isRetryableResumeHandoffClientError,
  isUnreadableResumeHandoffClientError,
  readStoredResumeHandoffAsync,
  type TerminalResumeHandoffErrorCode,
  type ResumeHandoffClaimResult,
} from '../../lib/resume-handoff';
import {
  CV_PARSE_RECOVERY_FALLBACK_COPY,
  getResumeHandoffParseRecovery,
  resolveCvParseRecoveryMessage,
  resolveImportedCvIncompleteMessage,
} from '../../lib/cv-parse-recovery';
import { skillStyleId } from './cv-preset-rows';

const ROUTE = '/onboarding/ai-input';

/**
 * Auto-import of a resume the user uploaded on the website (landing hero,
 * skill share page, public CV Health review) on the ai-input step. One hook
 * for both StepAIInput twins: the two copies it replaces had drifted, and the
 * rules below are the fix for incident 2026-10-04 (a brand-new user greeted by
 * "Resume import failed / this handoff has expired" without importing
 * anything).
 *
 * The claim runs only when BOTH hold:
 * 1. this sign-in came through the funnel: the per-sign-in post-auth target is
 *    `resume-import` (the same gate completeOnboarding uses, and the one the
 *    CV-handoff auto-claim keys off). A token with any other target is a
 *    leftover from an earlier session or account on this device and is
 *    discarded silently;
 * 2. the stored token is fresh: lib/resume-handoff-storage.ts ages every
 *    entry and discards legacy, aged or expired ones before they get here.
 *
 * Outcomes:
 * - success: token and target are consumed, the CV goes to the header step
 *   (an exact, no-AI extraction is confirmed first);
 * - a terminal code (not found / expired / claimed by another account /
 *   invalid): the upload is gone and no retry can help. Token and target are
 *   consumed and at most a calm note says to upload again. Never a red toast,
 *   never the server's English message;
 * - a recoverable parse (incomplete / provider unavailable, incident
 *   2026-09-02): token and target are KEPT so the import is retried later;
 * - anything else (unreadable file, transport): consumed, explained with the
 *   localized copy for that reason.
 */
export function useOnboardingResumeHandoffClaim({
  confirmExactExtraction,
}: {
  /** Review gate for a CV parsed WITHOUT AI; resolves false to keep the composer. */
  confirmExactExtraction: () => Promise<boolean>;
}) {
  const { t } = useTranslation('auth');
  const toast = useToast();
  const { persistImportedCvAndGoToHeader, applyCvPreset } = useOnboarding();
  const [pendingImport, setPendingImport] = useState<ResumeHandoffClaimResult | null>(null);
  const [sourceChanged, setSourceChanged] = useState(false);
  const [isClaimingHandoff, setIsClaimingHandoff] = useState(false);
  const attemptedRef = useRef(false);

  // Refs so the one-shot effect reads the latest callbacks without re-running.
  const persistRef = useRef(persistImportedCvAndGoToHeader);
  persistRef.current = persistImportedCvAndGoToHeader;
  const applyCvPresetRef = useRef(applyCvPreset);
  applyCvPresetRef.current = applyCvPreset;
  const confirmExactExtractionRef = useRef(confirmExactExtraction);
  confirmExactExtractionRef.current = confirmExactExtraction;

  useEffect(() => {
    if (attemptedRef.current) {
      return;
    }

    attemptedRef.current = true;

    const showUploadAgain = (code: TerminalResumeHandoffErrorCode | null) => {
      toast.show({
        type: 'info',
        title: t('onboarding.cvJourney.handoffUnavailableTitle', {
          defaultValue: 'Upload your CV again',
        }),
        message:
          code === 'HANDOFF_ALREADY_CLAIMED'
            ? t('onboarding.cvJourney.handoffAlreadyClaimedBody', {
                defaultValue:
                  'That CV has already been imported. Upload or paste a CV below to continue.',
              })
            : t('onboarding.cvJourney.handoffUnavailableBody', {
                defaultValue:
                  "Your CV from the website didn't carry over. Upload or paste it below to continue.",
              }),
      });
    };

    void (async () => {
      const [target, stored] = await Promise.all([
        readPostAuthTargetAsync(),
        readStoredResumeHandoffAsync(),
      ]);
      const cameFromFunnel = target === 'resume-import';

      if (stored.status === 'none') {
        return;
      }

      if (stored.status === 'stale') {
        // Already discarded by the read. Only someone who really came through
        // the funnel (and took longer than the upload lived) hears about it.
        trackProductEvent('resume_handoff_stale_token_discarded', {
          route: ROUTE,
          context: 'onboarding',
          reason: stored.reason,
          cameFromFunnel,
        });
        if (cameFromFunnel) {
          clearPostAuthTarget();
          showUploadAgain(null);
        }
        return;
      }

      if (!cameFromFunnel) {
        // A fresh-looking token without this sign-in's intent behind it: an
        // earlier session or account on this device left it. Not ours to claim.
        await clearResumeHandoffToken();
        trackProductEvent('resume_handoff_stale_token_discarded', {
          route: ROUTE,
          context: 'onboarding',
          reason: 'no_resume_import_target',
          cameFromFunnel: false,
        });
        return;
      }

      setIsClaimingHandoff(true);
      const claimStartedAt = Date.now();
      trackProductEvent('resume_handoff_claim_started_client', {
        route: ROUTE,
        context: 'onboarding',
      });
      return claimResumeHandoff(stored.token)
        .then(async (result) => {
          setPendingImport(result);
          await clearResumeHandoffToken();
          clearPostAuthTarget();
          trackProductEvent('resume_handoff_claim_completed_client', {
            route: ROUTE,
            context: 'onboarding',
            durationMs: Math.max(Date.now() - claimStartedAt, 0),
          });
          // Exact extraction (no AI): the user reviews before it is written.
          if (result.quality?.fallbackUsed) {
            const proceed = await confirmExactExtractionRef.current();
            if (!proceed) {
              return;
            }
          }
          // A public skill share page can attach a skill to the handoff:
          // persist the pick (chip + resume safety) and pass it straight into
          // this import's enrichment, since the ref only updates on render.
          const presetStyleId = result.skillId
            ? skillStyleId(result.skillId)
            : undefined;
          if (presetStyleId) {
            void applyCvPresetRef.current(presetStyleId);
          }
          await persistRef.current(result.parsed, {
            mode: 'replace',
            presetStyleId,
            quality: result.quality ?? null,
            importEvidenceId: result.importEvidenceId,
            source: 'handoff',
            cvHealthToken: result.cvHealthToken ?? null,
          });
          setPendingImport(null);
        })
        .catch((error) => {
          if (isCvImportReviewCancelled(error)) return;
          // A degraded parse (incomplete / provider unavailable) leaves the
          // server-side handoff claimable until it expires. Keep the token
          // too, so the import is retried rather than lost (incident
          // 2026-09-02). Every other failure consumes it.
          const retryable = isRetryableResumeHandoffClientError(error);
          const terminalCode = getTerminalResumeHandoffErrorCode(error);
          if (!retryable) {
            void clearResumeHandoffToken();
            clearPostAuthTarget();
          }
          trackProductEvent('resume_handoff_claim_failed_client', {
            route: ROUTE,
            context: 'onboarding',
            retryable,
            terminalCode,
            durationMs: Math.max(Date.now() - claimStartedAt, 0),
            ...getErrorObservabilityProperties(error),
          });

          if (terminalCode) {
            showUploadAgain(terminalCode);
            return;
          }

          if (isCvImportSourceChanged(error)) {
            setSourceChanged(true);
            toast.error(t("seeker:cv.importEvidence.sourceChanged"));
            return;
          }

          const recovery = getResumeHandoffParseRecovery(error);
          const message = recovery
            ? `${resolveCvParseRecoveryMessage(t, recovery, { reviewOffered: false })} ${t('onboarding.cvJourney.handoffRetryLater', { defaultValue: CV_PARSE_RECOVERY_FALLBACK_COPY.handoffRetryLater })}`
            : (resolveImportedCvIncompleteMessage(t, error) ??
              (isUnreadableResumeHandoffClientError(error)
                ? t('onboarding.cvJourney.fileTextUnreadable', {
                    defaultValue:
                      "We couldn't read enough text from this file. Try a text-based PDF or Word document, or paste the resume text.",
                  })
                : // The server's message is hard-coded English; the reason is
                  // always rendered from localized copy.
                  t('onboarding.cvJourney.handoffClaimFailedBody', {
                    defaultValue:
                      "We couldn't bring your CV over from the website. Upload or paste it below to continue.",
                  })));
          toast.error(
            t('onboarding.cvJourney.resumeImportFailedTitle', {
              defaultValue: 'Resume import failed',
            }),
            message,
          );
        })
        .finally(() => {
          setIsClaimingHandoff(false);
        });
    })();
  }, [t, toast]);

  const resumePendingImport = async () => {
    if (!pendingImport || isClaimingHandoff || sourceChanged) return;
    setIsClaimingHandoff(true);
    try {
      await persistRef.current(pendingImport.parsed, {
        mode: 'push', quality: pendingImport.quality,
        importEvidenceId: pendingImport.importEvidenceId, source: 'handoff',
        cvHealthToken: pendingImport.cvHealthToken,
      });
      setPendingImport(null);
    } catch (error) {
      if (isCvImportReviewCancelled(error)) return;
      if (isCvImportSourceChanged(error)) setSourceChanged(true);
      toast.error(isCvImportSourceChanged(error) ? t('seeker:cv.importEvidence.sourceChanged') : t('onboarding.cvJourney.resumeImportFailedTitle'));
    } finally {
      setIsClaimingHandoff(false);
    }
  };
  return { isClaimingHandoff, pendingImport, sourceChanged, resumePendingImport };
}
